// Libraries
import { select, selectAll, scalePow } from 'd3';
import scrollama from 'scrollama';
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Shape,
  ShapeGeometry,
  Mesh,
  MeshPhongMaterial,
  BoxGeometry,
  MeshBasicMaterial,
  DoubleSide,
  BoxHelper,
  TextureLoader,
  SRGBColorSpace,
  RepeatWrapping,
  Color,
  Texture,
  Vector3,
  Clock,
  BufferGeometry,
  PointsMaterial,
  BufferAttribute,
  Points,
  AdditiveBlending,
} from 'three';
import gsap from 'gsap';
import { widont } from 'journalize';

// Internal functions
import createFishShape1 from './shapes/fishShape1';
import createFishShape2 from './shapes/fishShape2';
import createFishShape3 from './shapes/fishShape3';
import createFishShape4 from './shapes/fishShape4';

// Data
import context from '../../data/text.json';
import data from '../../data/data.json';

/**
 * Setup
 */
const text = context['scrolly_3'];
const positionsData = data['statewide_position'];
const hd142Data = data['statewide_HD142'];
const hd142Index = hd142Data.filter(d => d.index != '').map(d => +d.index);
const scrollyData = data['statewide_scrolly'];
const annotationsData = data['scrolly_overlay_'];

const scroller = scrollama();

/**
 * Parameters
 */
const scrolly = 3;
const stepsCount = 6;
const maxAnnotationsCount = 7;
const count = 30030;
const colors = {
  red: '#d64946',
  blue: '#21789b',
  navy: '#000016',
  yellow: '#be9304',
  gray: '#808080',
  darkgray: '#434343',
  white: '#eeeeee',
  lightgray: '#C0C0C0',
  black: '#101010',
};
const vColors = {
  red: [0.8392156862745098, 0.28627450980392155, 0.27450980392156865],
  blue: [0.12941176470588237, 0.47058823529411764, 0.6078431372549019],
  yellow: [0.7450980392156863, 0.5764705882352941, 0.01568627450980392],
  gray: [0.5, 0.5, 0.5],
  white: [1, 1, 1],
  lightgray: [0.7529411764705882, 0.7529411764705882, 0.7529411764705882],
  darkgray: [0.2, 0.2, 0.2],
  black: [0.06274509803921569, 0.06274509803921569, 0.06274509803921569],
  navy: [0, 0, 0.08627450980392157],
};
const rem = 16;
let view = 'mobile';
let scrollyIndex;
let hasLoaded = false;
let zoom;
let currentPositions = new Float32Array(count * 3);
let defaultPositions = new Float32Array(count * 3);
let rgbs = new Float32Array(count * 3);

const params = {
  desktop: {
    aspectRatio: 5 / 9,
    meshMarginScale: { x: 70, y: 30 },
    meshScale: 0.85,
    meshTranslateScale: { x: 24, y: 20 },
    meshMarginScaleZoomedOut: { x: 7, y: 3 },
    meshRotationScale: 1.8,
    meshRotationDuration: 3,
    meshStaggerScale: 0.0025,
    cameraPosition: 800,
    cameraZoomedOutPosition: 1000,
    particlesSize: 3,
  },
  mobile: {
    aspectRatio: 16 / 9,
    meshMarginScale: { x: 42, y: 18 },
    meshScale: 0.5,
    meshTranslateScale: { x: 24, y: 20 },
    meshMarginScaleZoomedOut: { x: 7, y: 3 },
    meshRotationScale: 1.8,
    meshRotationDuration: 3,
    meshStaggerScale: 0.0025,
    cameraPosition: 1000,
    cameraZoomedOutPosition: 1400,
    particlesSize: 3,
  },
};

const breakpoints = [
  { cat: 'xl', width: 1600, defaultHeight: 1600 * 0.8 },
  { cat: 'l', width: 1140, defaultHeight: 1140 * 0.8 },
  { cat: 'm', width: 1000, defaultHeight: 1000 * 0.8 },
  { cat: 't', width: 800, defaultHeight: 800 * 0.8 },
  { cat: 'r', width: 664, defaultHeight: 664 * params[view].aspectRatio },
  { cat: 's', width: 540, defaultHeight: 540 * params[view].aspectRatio },
  { cat: 'xs', width: 370, defaultHeight: 370 * params[view].aspectRatio },
  { cat: 'xxs', width: 320, defaultHeight: 320 * params[view].aspectRatio },
];

/**
 * Could move to utils?
 */
const getWindowSize = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  return { width, height };
};

const getFigureSize = windowSize => {
  const figureSize = {};
  let index;

  if (windowSize.width > breakpoints[0].width) {
    figureSize.width = breakpoints[0].width;
    index = 0;
  } else if (windowSize.width > breakpoints[1].width) {
    figureSize.width = breakpoints[1].width;
    index = 1;
  } else if (windowSize.width > breakpoints[2].width) {
    figureSize.width = breakpoints[2].width;
    index = 2;
  } else if (windowSize.width > breakpoints[3].width) {
    figureSize.width = breakpoints[3].width;
    index = 3;
  } else if (windowSize.width > breakpoints[4].width) {
    figureSize.width = breakpoints[4].width;
    index = 4;
  } else if (windowSize.width > breakpoints[5].width) {
    figureSize.width = breakpoints[5].width;
    index = 5;
  } else if (windowSize.width > breakpoints[6].width) {
    figureSize.width = breakpoints[6].width;
    index = 6;
  } else {
    figureSize.width = 320;
    index = 7;
  }

  figureSize.height = breakpoints[index].defaultHeight;

  // if (figureSize.height > windowSize.height) {
  //   if (view = "desktop") {
  //     figureSize.height = figureSize.height <= breakpoints[1].defaultHeight
  //       ? breakpoints[1].defaultHeight
  //       : figureSize.height <= breakpoints[2].defaultHeight
  //       ? breakpoints[2].defaultHeight
  //       :
  //   } else {

  //   }
  // }

  return figureSize;
};

const getUseSize = figureSize => {
  const useSize = {};

  useSize.width = figureSize.width * 0.8;

  return useSize;
};

const getRandomInt = max => {
  return Math.floor(Math.random() * max);
};

/**
 * Elements
 */
const container = select('#scrolly_3');
const windowSize = getWindowSize();
view = windowSize.width > 800 ? 'desktop' : 'mobile';

// figure
const figure = container.append('figure');
const figureSize = getFigureSize(windowSize);

const progressBar = figure.append('div').classed('progress-bar', true);
const progressBubbles = progressBar
  .append('div')
  .classed('progress-bubbles', true)
  .selectAll('div.progress-bubble')
  .data([...Array(stepsCount).keys()])
  .join('div')
  .classed('progress-bubble', true)
  .attr('id', d => `progress-bubble__${d + 1}`)
  .attr('data-index', d => d);

const canvasContainer = figure.append('div').classed('canvas-container', true);

const canvas = canvasContainer.append('canvas');

const canvasOverlay = canvasContainer
  .append('div')
  .classed('canvas-overlay', true)
  .classed('canvas-overlay__inset-shadow', true);

// useG.selectAll('use')
//   .data(scrollyData)
//   .join('use')
//   .attr('width', useSize.width)
//   .attr('transform', `translate(${figureSize.width / 2 - useSize.width / 2 + rem / 2}, ${-rem * 2})`)
//   .attr('href', d => `#${d.fileName}`)
//   .attr('class', d => `use-${d.step}`)
//   .style('opacity', 0);

// const clip = svg.append('rect')
//   .attr('x', 0)
//   .attr('y', 0)
//   .attr('width', figureSize.width)
//   .attr('height', figureSize.height)
//   .attr('clip-path', 'url(#scrolly_3_clipPath)')

const annotations = canvasOverlay
  .append('div')
  .classed('annotation-container', true)
  .selectAll('div.annotation')
  .data([...Array(maxAnnotationsCount).keys()])
  .join('div')
  .classed('annotation', true)
  .attr('id', d => `annotation_${d + 1}`);
annotations.append('div').classed('label', true);
annotations.append('div').classed('number', true);

// article
const article = container.append('article');
const outerSteps = article
  .selectAll('div.step__outer')
  .data(text)
  .join('div')
  .classed('step__outer', true);
const innerSteps = outerSteps
  .append('div')
  .classed('step__inner', true)
  .attr('data-step-index', (d, i) => i);
innerSteps
  .append('p')
  .classed('t-copy', true)
  .text(d => d);

const resizeElements = () => {
  // update sizes
  const windowSize = getWindowSize();
  const figureSize = getFigureSize(windowSize);

  canvas.attr('width', figureSize.width).attr('height', figureSize.height);

  canvasOverlay
    .style('width', `${figureSize.width}px`)
    .style('height', `${figureSize.height}px`);

  setParticleDefaultAttributes();

  const newView = windowSize.width > 800 ? 'desktop' : 'mobile';
  if (view != newView) {
    view = newView;
    zoom =
      scrollyIndex == 0
        ? params[view].cameraZoomedOutPosition
        : params[view].cameraZoomedOutPosition;
    initFishGraphic();
  } else {
    camera = new PerspectiveCamera(75, figureSize.width / figureSize.height);
    camera.position.set(0, 0, params[view].cameraZoomedOutPosition);
    camera.lookAt(0, 0, 0);
    scene.add(camera);
    renderer.setSize(figureSize.width, figureSize.height);
  }

  renderer.render(scene, camera);
};

/**
 * Fish graphics
 */

let scene,
  camera,
  renderer,
  particles,
  particlesGeometry,
  particlesMaterial,
  particlesTexture;

const setParticleDefaultAttributes = () => {
  const maxX = 213;
  const maxY = 180;

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    const x = i3;
    const y = i3 + 1;
    const z = i3 + 2;

    const index = Math.floor(i);

    const data = positionsData.find(
      d => index >= d.index_min && index <= d.index_max
    );
    let positionX, positionY;

    if (view == 'desktop') {
      positionX = Math.floor(data.x - maxX / 2) + 1;
      positionY = -(
        (maxY - data.count) / 2 +
        (index - data.index_min) -
        maxY / 2 -
        14
      );

      if (positionY % 2 == 0) {
        positionX = positionX - 0.5;
      }

      currentPositions[x] = positionX * 6.8 + Math.random() * 10;
      currentPositions[y] =
        (positionY * 6.8 * params[view].meshMarginScale.y) /
          params[view].meshMarginScale.x +
        Math.random() * 10;
      currentPositions[z] = (Math.random() - 1) * 2;
    } else {
      positionY = Math.floor(data.x - maxX / 2) + 1;
      positionX =
        (maxY - data.count) / 2 + (index - data.index_min) - maxY / 2 - 8;

      if (positionX % 2 == 0) {
        positionY = positionY - 0.5;
      }

      currentPositions[x] = (positionX * 6.8) / 2 + Math.random() * 10;
      currentPositions[y] = positionY * 6.8 * 0.7 + Math.random() * 10;
      currentPositions[z] = (Math.random() - 1) * 2;
    }

    defaultPositions[x] = currentPositions[x];
    defaultPositions[y] = currentPositions[y];
    defaultPositions[z] = currentPositions[z];

    rgbs[x] = vColors.black[0];
    rgbs[y] = vColors.black[1];
    rgbs[z] = vColors.black[2];
  }
};

const setUpParticles = () => {
  // particles as fish
  particlesGeometry = new BufferGeometry();

  setParticleDefaultAttributes();

  particlesGeometry.setAttribute(
    'position',
    new BufferAttribute(currentPositions, 3)
  );

  particlesGeometry.setAttribute('color', new BufferAttribute(rgbs, 3));

  particlesMaterial = new PointsMaterial({
    size: 0,
    transparent: true,
    vertexColors: true,
    alphaTest: 0.001,
    sizeAttenuation: true,
    // blending: AdditiveBlending
  });
};

const center = {
  x: 65 + (145 - 65) / 2,
  y: 55 + (155 - 55) / 2,
};
const distMax = 40;
const rScale = scalePow()
  .domain([0, distMax])
  .range([67, 0]);
const gScale = rScale;
const bScale = scalePow()
  .domain([0, distMax])
  .range([67, 22]);

const getMeshColor = (x, y) => {
  const deltaX = Math.abs(x - center.x);
  const deltaY = Math.abs(y - center.y);
  const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

  const r = dist > distMax ? 0 : Math.round(rScale(dist));
  const g = dist > distMax ? 0 : Math.round(gScale(dist));
  const b = dist > distMax ? 22 : Math.round(bScale(dist));

  return `rgb(${r}, ${g}, ${b})`;
};

const initFishGraphic = () => {
  // scene
  scene = new Scene();
  scene.background = new Color(colors.navy);

  // camera
  camera = new PerspectiveCamera(75, figureSize.width / figureSize.height);
  camera.position.set(0, 0, params[view].cameraZoomedOutPosition);
  camera.lookAt(0, 0, 0);
  scene.add(camera);

  // mesh
  const geometries = [
    new ShapeGeometry(createFishShape1(new Shape())),
    new ShapeGeometry(createFishShape2(new Shape())),
    new ShapeGeometry(createFishShape3(new Shape())),
    new ShapeGeometry(createFishShape4(new Shape())),
  ];

  const fishTexture = new TextureLoader().load('assets/texture/white.png');
  fishTexture.colorSpace = SRGBColorSpace;
  fishTexture.wrapS = fishTexture.wrapT = RepeatWrapping;
  fishTexture.repeat.set(0.008, 0.008);

  hd142Data.forEach((fish, i) => {
    const geometry = geometries[getRandomInt(4)];
    const color = getMeshColor(fish.x, fish.y);
    const c = fish.cat === 'background' ? color : 'white';
    const material = new MeshBasicMaterial({ map: fishTexture, color: c });
    const mesh = new Mesh(geometry, material);

    let x = (fish.x - 107) * params[view].meshMarginScale.x;
    let y = -(fish.y - 106) * params[view].meshMarginScale.y;

    if (fish.y % 2 === 0) {
      x = (fish.x - 107 + 0.5) * params[view].meshMarginScale.x;
    }

    mesh.index = i;
    mesh.data = fish;
    mesh.defaultPosition = new Vector3(x, y, 3);
    mesh.position.set(x, y, 3);
    mesh.rotation.z = (Math.random() - 0.5) / params[view].meshRotationScale;
    mesh.scale.set(
      params[view].meshScale,
      params[view].meshScale,
      params[view].meshScale
    );

    gsap.to(mesh.position, {
      x: x + Math.random() * 10,
      y: y + Math.random() * 10,
      duration: 3 + Math.random(),
      repeat: -1,
      yoyo: true,
    });

    gsap.to(mesh.rotation, {
      z: (Math.random() - 0.5) / params[view].meshRotationScale,
      duration: params[view].meshRotationDuration + Math.random(),
      repeat: -1,
      yoyo: true,
    });

    scene.add(mesh);
  });

  // render
  renderer = new WebGLRenderer({
    canvas: canvas.node(),
  });
  renderer.setSize(figureSize.width, figureSize.height);
  renderer.render(scene, camera);
};

/**
 * Animation
 */
const clock = new Clock();
let request;
let elapsedTime = clock.getElapsedTime();
let stepTime = 0;
let animateTime = 0;
let deltaTime = 0;

const animateFishGraphic = () => {
  animateTime = clock.getElapsedTime();
  deltaTime = animateTime - stepTime;
  if (scrollyIndex == 0 && deltaTime > 1) {
    // // update particles
    // for (let i = 0; i < count; i++) {
    //   const i3 = i * 3;
    //   if(hd142Index.includes(i)) {
    //     particlesGeometry.attributes.position.array[i3] = 10000
    //     particlesGeometry.attributes.position.array[i3 + 1] = 10000
    //   } else {
    //     particlesGeometry.attributes.position.array[i3] = defaultPositions[i3] + Math.sin(elapsedTime + i % 194 ) / 5
    //     particlesGeometry.attributes.position.array[i3 + 1] = defaultPositions[i3 + 1] + Math.sin(elapsedTime + i % 194) / 5
    //   }
    //   particlesGeometry.attributes.position.array[i3 + 2] = defaultPositions[i3 + 2] + Math.sin(elapsedTime + i % 194) / 1.5
    // }
    // particlesGeometry.attributes.position.needsUpdate = true;
    // particlesGeometry.attributes.color.needsUpdate = true;
    // update meshes
    // const meshes = scene.children.filter(el => el.type == "Mesh");
    // meshes.forEach((mesh, i) => {
    //   if (view == "desktop") {
    //     mesh.position.y = mesh.defaultPosition.y + Math.sin(elapsedTime + mesh.defaultPosition.y + i) * 1.5;
    //   } else {
    //     mesh.position.x = mesh.defaultPosition.x + Math.sin(elapsedTime + mesh.defaultPosition.x + i) * 1.5;
    //   }
    //   mesh.position.z = (Math.sin(elapsedTime + i) + 3) * 5 ;
    // })
  }

  renderer.render(scene, camera);

  request = requestAnimationFrame(animateFishGraphic);
};

/**
 * Scroller Functions
 */
const handleStepEnter = response => {
  const { element, direction, index } = response;

  scrollyIndex = index;
  stepTime = clock.getElapsedTime();

  // useG.selectAll('use')
  //   .transition()
  //   .duration(500)
  //   .style('opacity', 0)
  // useG.selectAll(`.use-${index + 1}`)
  //   .transition()
  //   .duration(500)
  //   .style('opacity', 0.5)

  // canvas overlay
  if (index == 0) {
    canvasOverlay.classed('canvas-overlay__inset-shadow', true);
  } else {
    canvasOverlay.classed('canvas-overlay__inset-shadow', false);
  }

  // annotations
  const annotations = annotationsData.filter(
    d =>
      d.scrolly == scrolly && d.width == figureSize.width && d.step == index + 1
  );

  const annoSel = container.selectAll('.annotation');
  annoSel.select('div.label').html('');
  annoSel.select('div.number').html('');

  annotations.forEach(anno => {
    const sel = container.select(`#annotation_${anno.annotationId}`);

    sel
      .classed(anno.color, true)
      .style('top', `${anno.top}px`)
      .style('left', `${anno.left}px`)
      // .style('color', colors[anno.color])
      .style('text-align', anno.align);

    sel.select('div.label').html(widont(anno.text));
    sel.select('div.number').html(anno.number);
  });

  // update the camera
  if (index == 0) {
    gsap.to(camera.position, {
      z: params[view].cameraPosition,
      duration: 1,
    });
  } else if (index == 1) {
    // move the camera
    gsap.to(camera.position, {
      z: params[view].cameraZoomedOutPosition,
      duration: 1,
    });
  } else if (index >= 2) {
    camera.position.z = params[view].cameraZoomedOutPosition;
  }

  // update particles
  const particlesAdded = scene.children.filter(el => el.type == 'Points');
  if (scrollyIndex == 0) {
    if (particlesAdded.length == 1) {
      scene.remove(particles);
    }
  } else {
    if (particlesAdded.length == 0) {
      // particles
      setUpParticles();
      particles = new Points(particlesGeometry, particlesMaterial);
      scene.add(particles);
    }

    particlesMaterial.size = params[view].particlesSize;
    particlesMaterial.needsUpdate = true;
  }

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    if (scrollyIndex == 1) {
      particlesGeometry.attributes.position.array[i3] =
        defaultPositions[i3] + Math.random() * 10;
      particlesGeometry.attributes.position.array[i3 + 1] =
        defaultPositions[i3 + 1] + Math.random() * 10;

      if (hd142Index.includes(i)) {
        particlesGeometry.attributes.color.array[i3] = vColors.white[0];
        particlesGeometry.attributes.color.array[i3 + 1] = vColors.white[1];
        particlesGeometry.attributes.color.array[i3 + 2] = vColors.white[2];
      } else {
        particlesGeometry.attributes.color.array[i3] = vColors.black[0];
        particlesGeometry.attributes.color.array[i3 + 1] = vColors.black[1];
        particlesGeometry.attributes.color.array[i3 + 2] = vColors.black[2];
      }
    }

    if (scrollyIndex >= 2) {
      const col = scrollyData.find(
        d => d.step == scrollyIndex + 1 && d.index_min <= i && d.index_max >= i
      );

      let varIndex;
      let pPosition, pColor, vColor;

      if (col != undefined) {
        if (col.variable == 1) {
          varIndex = 1;
        } else if (col.variable == 2) {
          varIndex = col.index_min + col.count_1 > i ? 1 : 2;
        } else if (col.variable == 3) {
          varIndex =
            col.index_min + col.count_1 > i
              ? 1
              : col.index_min + col.count_1 + col.count_2 > i
              ? 2
              : 3;
        } else if (col.variable == 4) {
          varIndex =
            col.index_min + col.count_1 > i
              ? 1
              : col.index_min + col.count_1 + col.count_2 > i
              ? 2
              : col.index_min + col.count_1 + col.count_2 + col.count_3 > i
              ? 3
              : 4;
        }

        pPosition = col[`position_${varIndex}`];
        pColor = col[`color_${varIndex}`];
        vColor = vColors[pColor];
      } else {
        pPosition = 0;
        pColor = 'white';
        vColor = vColors[pColor];
      }

      if (view == 'desktop') {
        currentPositions[i3] =
          defaultPositions[i3] + pPosition * 50 + Math.random() * 10;
        currentPositions[i3 + 1] =
          defaultPositions[i3 + 1] + Math.random() * 10;
        currentPositions[i3 + 2] =
          defaultPositions[i3 + 2] + Math.random() * 10;
      } else {
        currentPositions[i3] = defaultPositions[i3] + Math.random() * 10 * 3;
        currentPositions[i3 + 1] =
          defaultPositions[i3 + 1] + pPosition * 50 + (Math.random() * 10) / 3;
        currentPositions[i3 + 2] =
          defaultPositions[i3 + 2] + Math.random() * 10;
      }

      particlesGeometry.attributes.position.array[i3] = currentPositions[i3];
      particlesGeometry.attributes.position.array[i3 + 1] =
        currentPositions[i3 + 1];
      particlesGeometry.attributes.position.array[i3 + 2] =
        currentPositions[i3 + 2];

      // color
      particlesGeometry.attributes.color.array[i3] = vColor[0];
      particlesGeometry.attributes.color.array[i3 + 1] = vColor[1];
      particlesGeometry.attributes.color.array[i3 + 2] = vColor[2];
    }

    particlesGeometry.attributes.position.needsUpdate = true;
    particlesGeometry.attributes.color.needsUpdate = true;
  }

  // if (index == 0) {
  //   // setTimeout(() => {
  //   //   request = requestAnimationFrame(animateFishGraphic)
  //   // }, 1001)
  // } else {
  //   cancelAnimationFrame(request);
  // }

  // update the mesh
  const meshes = scene.children.filter(el => el.type == 'Mesh');

  meshes.forEach((mesh, i) => {
    const data = mesh.data;

    // rotation
    gsap.to(mesh.rotation, {
      z: (Math.random() - 0.5) / params[view].meshRotationScale,
      duration: params[view].meshRotationDuration + Math.random(),
      repeat: -1,
      yoyo: true,
    });

    if (index == 0 && hasLoaded == false) {
      let x = (data.x - 107) * params[view].meshMarginScale.x;
      let y = -(data.y - 106) * params[view].meshMarginScale.y;

      if (data.y % 2 === 0) {
        x = (data.x - 107 + 0.5) * params[view].meshMarginScale.x;
      }

      mesh.position.x = x;
      mesh.position.y = y;

      gsap.to(mesh.position, {
        x: x + Math.random() * 10,
        y: y + Math.random() * 10,
        duration: 3 + Math.random(),
        repeat: -1,
        yoyo: true,
      });

      (mesh.scale.x = params[view].meshScale),
        (mesh.scale.y = params[view].meshScale),
        (mesh.scale.z = params[view].meshScale);
    } else if (index == 0 && hasLoaded == true) {
      let x = (data.x - 107) * params[view].meshMarginScale.x;
      let y = -(data.y - 106) * params[view].meshMarginScale.y;

      if (data.y % 2 === 0) {
        x = (data.x - 107 + 0.5) * params[view].meshMarginScale.x;
      }

      gsap.to(mesh.position, {
        x: x,
        y: y,
        duration: 1,
      });

      gsap.to(mesh.position, {
        x: x + Math.random() * 10,
        y: y + Math.random() * 10,
        delay: 1,
        duration: 3 + Math.random(),
        repeat: -1,
        yoyo: true,
      });

      gsap.to(mesh.scale, {
        x: params[view].meshScale,
        y: params[view].meshScale,
        z: params[view].meshScale,
        duration: 1,
      });
    } else {
      let x = (data.x - 103) * params[view].meshMarginScaleZoomedOut.x;
      let y = -(data.y - 106) * params[view].meshMarginScaleZoomedOut.y;

      if (data.y % 2 === 0) {
        x = (data.x - 103 + 0.5) * params[view].meshMarginScaleZoomedOut.x;
      }

      gsap.to(mesh.position, {
        x: x,
        y: y,
        duration: 1,
      });

      gsap.to(mesh.position, {
        x: x + Math.random() * 10,
        y: y + 100,
        delay: 1,
        duration: 5,
        repeat: -1,
        yoyo: true,
      });

      gsap.to(mesh.scale, {
        x: 0,
        y: 0,
        z: 0,
        duration: 1,
      });
    }
  });

  renderer.render(scene, camera);

  hasLoaded = true;
};

const handleStepProgress = response => {
  console.log('progress: ', response);

  progressBubbles.style('background-color', colors.navy).each(function() {
    const index = select(this).attr('data-index');
    const id = select(this).attr('id');
    if (index === response.index) {
      select(this)
        .style('background-color', colors.white)
        .style(
          'top',
          `${windowSize.height * 0.8 * -(response.progress - 1)}px`
        );
    }
  });

  const progressBubble = select(`#progress-bubble__${response.index + 1}`);
  // .attr('data-top', d => d * 6 * 2)
  // .attr('data-bottom', d => (-(d - 5) * 6 * 2))
  const top = progressBubble.attr('data-top');
  const bottom = progressBubble.attr('data-bottom');

  progressBubble.style('background-color', colors.white);
};

/**
 * Resize
 */
const handleResize = () => {
  resizeElements();
  scroller.resize();
};

addEventListener('resize', handleResize);

const initScroller = () => {
  handleResize();
  scroller
    .setup({
      step: '#scrolly_3 article .step__outer',
      offset: 0.75,
      progress: true,
      threshold: 100,
    })
    .onStepEnter(handleStepEnter);
  // .onStepProgress(handleStepProgress);
};

/**
 * Instantiate
 */
initFishGraphic();
animateFishGraphic();
initScroller();
